/**
 * Today's pulse — light 24h-vs-48h diff card auto-refreshed every 60s.
 *
 * Backend shape (getTodayPulse):
 *   {
 *     signups_24h: number,
 *     signups_24h_prior: number,
 *     signups_wow_pct: number | null,
 *     match_ready_24h: number,
 *     activation_rate_24h: number | null,
 *   }
 */
import { formatNumber } from '@/lib/utils';

interface PulseData {
  signups_24h: number;
  signups_24h_prior: number;
  signups_wow_pct: number | null;
  match_ready_24h: number;
  activation_rate_24h: number | null;
}

function pct(n: number | null): string {
  if (n == null) return '—';
  return `${n.toFixed(1)}%`;
}

function trendArrow(n: number | null): { glyph: string; color: string } {
  if (n == null) return { glyph: '', color: 'text-muted-foreground' };
  if (n >= 0) return { glyph: '↗', color: 'text-green-600' };
  return { glyph: '↘', color: 'text-red-600' };
}

export function TodayPulseCard({ data }: { data: PulseData }) {
  const trend = trendArrow(data.signups_wow_pct);
  return (
    <section className="bg-card border rounded-lg p-6">
      <h3 className="text-lg font-semibold mb-1">Today's pulse</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Live, 60s auto-refresh — last 24h vs the prior 24h.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Signups (24h)</div>
          <div className="flex items-baseline gap-2 mt-1">
            <div className="text-3xl font-bold">{formatNumber(data.signups_24h)}</div>
            {data.signups_wow_pct != null && (
              <div className={`text-sm font-medium ${trend.color}`}>
                {trend.glyph} {Math.abs(data.signups_wow_pct).toFixed(1)}%
              </div>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            vs prior 24h: {formatNumber(data.signups_24h_prior)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">
            Match-ready (24h)
          </div>
          <div className="text-3xl font-bold mt-1">{formatNumber(data.match_ready_24h)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">
            Activation rate (24h)
          </div>
          <div className="text-3xl font-bold mt-1">{pct(data.activation_rate_24h)}</div>
          <div className="text-xs text-muted-foreground mt-1">match-ready / signups</div>
        </div>
      </div>
    </section>
  );
}
